import React from 'react';
import {
	Box,
	BoxProps,
	Button,
	Flex,
	IconButton,
	Image,
	Modal,
	ModalBody,
	ModalContent,
	ModalOverlay,
	Text,
	TextProps,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { Icon } from '../../components';
import { useGetWebinarLinks } from '../../hooks';

interface GuestModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	underlineTitle: string;
	afterUnderlineTitle?: string;
	subtitle?: string;
	highlightedSubtitle: string | React.ReactNode;
	buttonName: string;
	imageScreen: string;
	secondSubtitle?: string;
	move?: boolean;
	afterUnderlineTitleMove?: boolean;
	contentPartContainerProps?: BoxProps;
	titleProps?: TextProps;
}

const GuestModal = ({
	isOpen,
	onClose,
	title,
	underlineTitle,
	afterUnderlineTitle = '',
	subtitle,
	highlightedSubtitle,
	buttonName,
	imageScreen,
	secondSubtitle,
	contentPartContainerProps,
	titleProps,
	afterUnderlineTitleMove = false,
	move = false,
}: GuestModalProps) => {
	const { data: webinarLinks, isPending } = useGetWebinarLinks(isOpen);
	const isWebinarLinksLoading = isPending && !webinarLinks;

	const getNextWednesdayAt9PM = () => {
		const now = dayjs().tz('America/New_York');
		const thisWednesday = now.day(3).hour(21).minute(0).second(0);

		return now.isBefore(thisWednesday)
			? thisWednesday
			: thisWednesday.add(1, 'week');
	};

	const nextWednesday = getNextWednesdayAt9PM().format('MMM D');
	return (
		<Modal
			isCentered={true}
			size={{ base: 'full', md: '3xl' }}
			returnFocusOnClose={false}
			isOpen={isOpen}
			onClose={onClose}>
			<ModalOverlay backdropFilter="blur(3px)" />
			<ModalContent
				borderRadius={{ base: 0, md: '10px' }}
				maxH={{ base: '100dvh', md: 'calc(100dvh - 3.75rem)' }}
				overflowY="auto">
				<ModalBody p={0}>
					<Flex minH={{ base: '100dvh', md: 'auto' }}>
						<Box
							position="relative"
							bg={'blue.100'}
							w={'285px'}
							h={'420px'}
							display={{ base: 'none', md: 'block' }}
							borderRadius={'5px 0 0 5px'}>
							<Image
								position={'absolute'}
								top={'50%'}
								left={'50%'}
								transform={['translate(-50%, -50%)']}
								src={imageScreen}></Image>
						</Box>
						<Box
							w={{ base: 'full', md: '483px' }}
							mt={'20px'}
							{...contentPartContainerProps}>
							<Flex justifyContent={'flex-end'} mr={'20px'}>
								<IconButton
									background={'none'}
									aria-label="close"
									icon={
										<Icon
											name="x-close"
											width="24px"
											height="24px"
											color="#1C2959"
										/>
									}
									onClick={onClose}
								/>
							</Flex>
							<Flex
								flexDirection={'column'}
								justifyContent={'center'}
								alignItems={'center'}
								p={{
									base: '40px 20px 32px',
									md: '72px 40px 0',
								}}>
								<Text
									mb={'16px'}
									color={'blue.200'}
									fontWeight={700}
									variant={'bodyLarge'}
									{...titleProps}>
									{title}
									<span
										style={{
											textDecoration: 'underline',
											textDecorationColor: '#08BB4B',
											textUnderlineOffset: '5px',
										}}>
										{underlineTitle}
									</span>
									{afterUnderlineTitle ? (
										<>
											{!afterUnderlineTitleMove ? (
												<br />
											) : null}
											{afterUnderlineTitle}
										</>
									) : null}
								</Text>
								<Text
									lineHeight={'24px'}
									paddingX={'16px'}
									paddingY={'5px'}
									textAlign={'center'}
									backgroundColor={'blue.100'}
									mb={'32px'}
									color={'#FFFFFF'}
									fontWeight={500}
									variant={'bodyMedium'}>
									<Text
										as="span"
										fontWeight={700}
										color={'#08BB4B'}>
										{highlightedSubtitle}
									</Text>{' '}
									<br />
									on {nextWednesday} at 8 PM EST
									{subtitle}
									{secondSubtitle ? (
										<>
											{!move ? <br /> : null}
											{secondSubtitle}
										</>
									) : null}
								</Text>
								<Button
									w={'200px'}
									bg="success"
									color="white"
									variant={'green'}
									isDisabled={!webinarLinks?.presaleLink}
									isLoading={isWebinarLinksLoading}
									onClick={() => {
										if (webinarLinks?.presaleLink) {
											window.open(
												webinarLinks.presaleLink,
												'_blank',
												'noopener,noreferrer',
											);
										}
									}}
									_hover={{
										bg: 'green.300',
									}}>
									{buttonName}
								</Button>
							</Flex>
						</Box>
					</Flex>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};
export default GuestModal;
